import { useEffect, useMemo, useRef, useState } from "react";
import { validateIfcPreflight } from "../lib/api";

/**
 * V2.0.38 — Shared hook for free pre-flight IFC validation.
 *
 * Polls `/api/designs/{id}/ifc/validate` (which builds the IFC
 * in-memory + runs `ifcopenshell.validate` without consuming an unlock
 * or credit) on a heavily-debounced tick whenever the blueprint
 * mutates. Lives in the Live Inspector right rail so users see a
 * green/amber/red chip BEFORE clicking checkout.
 *
 * Returns: { result: { passed, error_count, errors, byte_count } | null,
 *           loading: boolean }
 */
export default function useIfcValidation(designId, blueprint) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  // Stable signature — only re-fetch when an architectural mutation
  // actually changes geometry (room count, area), NOT on every
  // selection tick or pan/zoom.
  const blueprintKey = useMemo(() => {
    if (!blueprint?.floors) return "";
    const parts = [];
    let totalArea = 0;
    let doorCount = 0;
    let windowCount = 0;
    for (const f of blueprint.floors) {
      const rs = (f.rooms || []).filter((r) => r?.deleted !== true);
      parts.push(`${f.floor_index || "?"}:${rs.length}`);
      for (const r of rs) {
        totalArea += (Number(r.width) || 0) * (Number(r.depth) || 0);
      }
      doorCount += (f.doors || []).filter((d) => d?.deleted !== true).length;
      windowCount += (f.windows || []).filter((w) => w?.deleted !== true).length;
    }
    return `${parts.join("|")}|${totalArea.toFixed(1)}|d${doorCount}|w${windowCount}`;
  }, [blueprint]);

  useEffect(() => {
    if (!designId || !blueprintKey) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    let cancelled = false;
    debounceRef.current = setTimeout(() => {
      setLoading(true);
      validateIfcPreflight(designId)
        .then((r) => {
          if (!cancelled) setResult(r.data);
        })
        .catch(() => {
          if (!cancelled) setResult(null);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 2500); // longer debounce — validation is cheaper but less urgent than code-compliance
    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [designId, blueprintKey]);

  return { result, loading };
}
